import { z } from 'zod'

/**
 * Types and schemas for site-related Firebase Functions
 */

// Zod schema for CreateSiteRequest validation
export const CreateSiteRequestSchema = z.object({
  name: z
    .string()
    .min(1, 'Site name is required')
    .max(100, 'Site name must be less than 100 characters'),
  address: z
    .string()
    .min(1, 'Address is required')
    .max(200, 'Address must be less than 200 characters'),
  city: z
    .string()
    .min(1, 'City is required')
    .max(100, 'City must be less than 100 characters'),
  state: z
    .string()
    .length(2, 'State must be a 2-character abbreviation')
    .regex(/^[A-Z]{2}$/, 'State must be uppercase letters'),
  zip: z
    .string()
    .regex(
      /^\d{5}(-\d{4})?$/,
      'ZIP code must be in format 12345 or 12345-6789',
    ),
  phone: z
    .string()
    .regex(/^[\d\s\-()]+$/, 'Invalid phone number format')
    .optional()
    .or(z.literal('')),
  contactName: z
    .string()
    .max(100, 'Contact name must be less than 100 characters')
    .optional()
    .or(z.literal('')),
})

export type CreateSiteRequest = z.infer<typeof CreateSiteRequestSchema>

export interface CreateSiteResponse {
  siteId: string
  message: string
}

// Archive / Unarchive site (single callable, boolean flag selects action)
export const SetSiteArchiveStatusRequestSchema = z.object({
  siteId: z.string().min(1, 'Site ID is required'),
  archived: z.boolean(),
  // Super admins must pass companyId. Company admins can omit; claims resolve it.
  companyId: z.string().min(1).optional(),
})

export type SetSiteArchiveStatusRequest = z.infer<
  typeof SetSiteArchiveStatusRequestSchema
>

export interface SetSiteArchiveStatusResponse {
  /** Number of units whose archivedAt was mutated by this action. */
  affectedUnitCount: number
  message: string
}

// Structured error response for validation failures
export interface ValidationError {
  field: string
  message: string
}

export interface StructuredError {
  code: string
  message: string
  validationErrors?: ValidationError[]
}
